import {
  CalendarEvent as SchemaCalendarEvent,
  EventStatus,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { CalendarColumnGroup } from '@frontend/calendar-grid';
import { getFullName } from '@frontend/name';
import { ScheduleRequestEvent } from '@frontend/schedule-helpers';
import {
  getOverlappingEventIndicatorVariant,
  OverlappingEventIndicatorVariantType,
} from '../../components/calendar-view/calendar-view-v3/overlapping-event-indicator';
import {
  GridAppointmentCalendarEvent,
  GridCalendarEvent,
  GridScheduleRequestCalendarEvent,
} from '../../components/calendar-view/types';
import { getLocalTimezone } from '../../utils/date-helpers';
import { CalendarEntitySchedule } from './calendar-schedule-definitions';

dayjs.extend(customParseFormat);

// ─── GridEventTiming ──────────────────────────────────────────────────────────

export type GridEventTiming = {
  startTime: string; // 'HH:mm', clipped to the visible calendarDate's grid rows
  duration: number; // minutes, clipped to the visible day
  isYesterdayEvent: boolean;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
};

/**
 * Clips an event's absolute start/end (which may span midnight) to the single day the grid renders,
 * mirroring getEventPlacementValues in calendar-view-v3/helper.ts but for the grid's minute/row model.
 * Sets isYesterdayEvent/overlappingIndicatorVariant so GridAppointmentCard/GridScheduleRequestCard can
 * render the existing "continued"/"+1 day" indicator.
 */
export function getGridEventTiming(
  calendarDate: string, // 'MM/DD/YYYY'
  startDate: string | undefined, // 'YYYY-MM-DD'
  startTime: string | undefined, // 'HH:mm:ss'
  endDate: string | undefined, // 'YYYY-MM-DD'
  endTime: string | undefined // 'HH:mm:ss'
): GridEventTiming {
  const dayStart = dayjs(calendarDate, 'MM/DD/YYYY');
  const eventStart = dayjs(`${startDate} ${startTime}`);
  const eventEnd = dayjs(`${endDate} ${endTime}`);

  const fallbackStartTime = startTime ? dayjs(startTime, 'HH:mm:ss').format('HH:mm') : '00:00';
  if (!dayStart.isValid() || !eventStart.isValid() || !eventEnd.isValid()) {
    return { startTime: fallbackStartTime, duration: 0, isYesterdayEvent: false };
  }

  const isYesterdayEvent = eventStart.isBefore(dayStart, 'day');
  const isOverlappingEvent = eventEnd.isAfter(dayStart.endOf('day'));

  const clippedStartMinutes = isYesterdayEvent ? 0 : eventStart.diff(dayStart.startOf('day'), 'minute');
  const clippedEndMinutes = isOverlappingEvent ? 24 * 60 : eventEnd.diff(dayStart.startOf('day'), 'minute');
  const totalDurationMinutes = clippedEndMinutes - clippedStartMinutes;

  return {
    startTime: dayjs().startOf('day').add(clippedStartMinutes, 'minute').format('HH:mm'),
    duration: Math.max(clippedEndMinutes - clippedStartMinutes, 0),
    isYesterdayEvent,
    overlappingIndicatorVariant:
      isYesterdayEvent || isOverlappingEvent ? getOverlappingEventIndicatorVariant(totalDurationMinutes) : undefined,
  };
}

// ─── Schedule-request event builder ──────────────────────────────────────────

type ScheduleRequestEntityOverrides = {
  entityId?: string;
  entityName?: string;
};

/**
 * Converts a ScheduleRequestEvent + pre-computed timing into the GridScheduleRequestCalendarEvent
 * shape expected by the calendar grid widget.
 *
 * The optional `entityOverrides` parameter carries entityId/entityName, which differ between the
 * operatory and provider call sites.
 *
 * ref: getMappedScheduleRequestData in use-get-calendar-events-v3.ts
 */
export function buildScheduleRequestGridEvent(
  sr: ScheduleRequestEvent,
  timing: GridEventTiming,
  getScopeName: (id: string) => string,
  calendarDate: string,
  entityOverrides?: ScheduleRequestEntityOverrides
): GridScheduleRequestCalendarEvent {
  const { startTime, duration, isYesterdayEvent, overlappingIndicatorVariant } = timing;
  return {
    id: sr.request.id || '',
    startTime,
    duration,
    isYesterdayEvent,
    overlappingIndicatorVariant,
    continuesFromPreviousDay: isYesterdayEvent,
    continuesToNextDay: !isYesterdayEvent && !!overlappingIndicatorVariant,
    eventType: 'scheduleRequest',
    scheduleRequestEventData: sr.request,
    locationId: sr.locationId,
    locationName: getScopeName(sr.locationId ?? ''),
    startDate: sr.startDate,
    startHour: dayjs(sr.startTime, 'HH:mm:ss').format('hh:mm A'),
    endHour: dayjs(sr.endTime, 'HH:mm:ss').format('hh:mm A'),
    eventTypeColor: sr.displayOptions.primaryColor,
    backgroundColor: sr.displayOptions.backgroundColor,
    calendarDate,
    appointmentTypeName: sr.appointmentTypeName,
    ...entityOverrides,
  };
}

// ─── Working-hours / breaks / exceptions event builder ────────────────────────

export type ScheduleGridEvents = { events: GridCalendarEvent[]; ids: string[] };

/**
 * Converts an entity's resolved working hours (including intra-day breaks) and resolved exceptions
 * into the flat list of `workingHoursV3` / `nonWorkingHoursV3` grid events expected by the widget.
 *
 * The returned `ids` array is a convenience so callers can push them straight into `eventIds`
 * without a second `.map()`.
 */
export function buildWorkingHoursAndBreaksEvents(
  schedule: CalendarEntitySchedule | undefined,
  entityId: string,
  entityName?: string
): ScheduleGridEvents {
  const events: GridCalendarEvent[] = [];
  const ids: string[] = [];

  (schedule?.resolvedWorkingHours ?? []).forEach((wh) => {
    const startTime = dayjs(wh.startTime, 'hh:mm A').format('HH:mm');
    const id = `working-hours-${entityId}-${wh.startTime}-${wh.duration}`;
    events.push({ id, startTime, duration: wh.duration, eventType: 'workingHoursV3', entityId, entityName });
    ids.push(id);

    (wh.breaks ?? []).forEach((b) => {
      const breakStartTime = dayjs(b.startTime, 'hh:mm A').format('HH:mm');
      const breakId = `working-hours-break-${entityId}-${b.startTime}-${b.duration}`;
      events.push({
        id: breakId,
        startTime: breakStartTime,
        duration: b.duration,
        eventType: 'nonWorkingHoursV3',
        entityId,
        entityName,
      });
      ids.push(breakId);
    });
  });

  (schedule?.resolvedExceptions ?? []).forEach((ex) => {
    const startTime = dayjs(ex.startTime, 'hh:mm A').format('HH:mm');
    const id = `working-hours-${entityId}-${ex.startTime}-${ex.duration}`;
    events.push({ id, startTime, duration: ex.duration, eventType: 'nonWorkingHoursV3', entityId, entityName });
    ids.push(id);
  });

  return { events, ids };
}

// ─── Column group builder ─────────────────────────────────────────────────────

type ColumnEntityOrderPreference = Partial<Record<'operatory' | 'provider', Record<string, Record<string, number>>>>;

export type BuildColumnGroupParams = {
  locationId: string;
  columnIds: string[];
  groupOffset: number;
  calendarLocationIds: string[];
  getScopeName: (id: string) => string;
  locationTimezoneMap: Record<string, { timezone?: string } | undefined>;
  orderKey: 'operatory' | 'provider';
  columnEntityOrder: ColumnEntityOrderPreference | undefined;
  emptyStateVariant?: 'no-events' | 'no-columns';
};

/**
 * Applies a saved column order map (columnId → orderIndex) to a list of column IDs.
 * Unassigned columns (id starts with "unassigned-") are always kept at the very end.
 */
export function applyColumnOrder(columnIds: string[], orderMap: Record<string, number> | undefined): string[] {
  if (!orderMap || Object.keys(orderMap).length === 0) return columnIds;

  const unassigned = columnIds.filter((id) => id.startsWith('unassigned-'));
  const ordered = columnIds.filter((id) => !id.startsWith('unassigned-'));

  ordered.sort((a, b) => {
    const orderA = orderMap[a] ?? Infinity;
    const orderB = orderMap[b] ?? Infinity;
    return orderA - orderB;
  });

  return [...ordered, ...unassigned];
}

/**
 * Builds a single CalendarColumnGroup entry for one location, applying the saved column order and
 * resolving the location timezone. `orderKey` ('operatory' | 'provider') is the only difference
 * between the two branches.
 */
export function buildColumnGroup({
  locationId,
  columnIds,
  groupOffset,
  calendarLocationIds,
  getScopeName,
  locationTimezoneMap,
  orderKey,
  columnEntityOrder,
  emptyStateVariant,
}: BuildColumnGroupParams): CalendarColumnGroup {
  return {
    id: locationId,
    name: calendarLocationIds.length > 1 ? getScopeName(locationId) : '',
    columnIds: applyColumnOrder(columnIds, columnEntityOrder?.[orderKey]?.[locationId]),
    groupOffset,
    timezone: locationTimezoneMap[locationId]?.timezone || getLocalTimezone(),
    emptyStateVariant,
  };
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

/** Derives a display name from the calendar event's attendee details. */
export const getAttendeeName = (calendarEvent: SchemaCalendarEvent): string =>
  (calendarEvent.attendeeDetails && getFullName(calendarEvent.attendeeDetails)) || 'Unknown';

// ─── Appointment event builder ────────────────────────────────────────────────

type AppointmentEntityOverrides = {
  entityId?: string;
  entityName?: string;
  /** Provider calendar ID — omit for operatory view (schema does not expose one) */
  calendarId?: string;
};

/**
 * Converts a raw SchemaCalendarEvent + pre-computed timing into the GridAppointmentCalendarEvent
 * shape expected by the calendar grid widget.
 *
 * The optional `entityOverrides` parameter carries the entity-specific fields that differ between
 * the four call sites (operatory entity, operatory unassigned, provider entity, provider unassigned).
 *
 * ref: getMappedCalendarEventData in use-get-calendar-events-v3.ts
 */
export function buildAppointmentGridEvent(
  event: SchemaCalendarEvent,
  timing: GridEventTiming,
  getScopeName: (id: string) => string,
  entityOverrides?: AppointmentEntityOverrides
): GridAppointmentCalendarEvent {
  const { startTime, duration, isYesterdayEvent, overlappingIndicatorVariant } = timing;
  return {
    id: event.id || '',
    startTime,
    duration,
    title: getAttendeeName(event),
    isYesterdayEvent,
    overlappingIndicatorVariant,
    continuesFromPreviousDay: isYesterdayEvent,
    continuesToNextDay: !isYesterdayEvent && !!overlappingIndicatorVariant,
    eventType: [EventStatus.CANCELED, EventStatus.NO_SHOW].includes(event.attendeeStatus as EventStatus)
      ? 'cancelledAppointment'
      : 'appointment',
    eventStatus: event.attendeeStatus,
    eventTypeColor: event.displayOptions?.primaryColor,
    backgroundColor: event.displayOptions?.backgroundColor,
    displayOptions: {
      primaryColor: event.displayOptions?.primaryColor,
      backgroundColor: event.displayOptions?.backgroundColor,
      textColor: event.displayOptions?.textColor,
    },
    // Drag-drop enriched fields
    startHour: dayjs(event.startTime, 'HH:mm:ss').format('hh:mm A'),
    endHour: dayjs(event.endTime, 'HH:mm:ss').format('hh:mm A'),
    startDate: event.startDate,
    endDate: event.endDate,
    locationId: event.locationId,
    locationName: getScopeName(event.locationId ?? ''),
    patientId: event.attendeeId,
    referenceId: event.referenceId,
    appointmentTypeId: event.referenceTypeId,
    appointmentTypeName: event.details?.rTyNa,
    // Entity-specific overrides
    ...entityOverrides,
  };
}
